import { useDisclosure } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFilePicker } from 'use-file-picker';
import { useCompaniesContext } from '../../../../contexts/companies';
import useRouter from '../../../../hooks/router/useRouter';
import { useReadDocumentsV2, useReadDocumentTypes, } from '../../../../services/online/repository/repository';
import { DocumentGroupBy, DocumentRelation, Role, SortBy, } from '../../../../services/types';
import { tKeys } from '../../../../translations/keys';
import { isMinute } from '../../../../utils/getDocumentKind';
import handleSelectDocument from '../../../../utils/handleSelectDocument';
import { onSelectedFileSuccess } from '../../../../utils/selectFile.web';
import { DocumentKindEnum, } from '../../../document-details/type';
import { ListLayoutEnum } from './type';
const useLogic = () => {
    // Attributes
    const { push, params } = useRouter();
    const { t } = useTranslation();
    const { selectedCompanyId, selectedEnrolment } = useCompaniesContext();
    const [showModal, setShowModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState();
    const [searchTerm, setSearchTerm] = useState('');
    const { isOpen: isOpenCreateDocument, onOpen: onOpenCreateDocument, onClose: onCloseCreateDocument, } = useDisclosure();
    // Rename and change type, map initial data from useReadDocumentTypes
    const [documentTypeOptions, setDocumentTypeOptions] = useState([]);
    // State for selected document types, now using IPickerSelectItem[]
    const [selectedDocumentTypes, setSelectedDocumentTypes] = useState([]);
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    // Default selected tab to index 1 ('From Event')
    const [selectedTab, setSelectedTab] = useState(0);
    const [listLayoutType, setListLayoutType] = useState(ListLayoutEnum.ROW);
    // Entity
    const repositoryFiltersEntity = params?.repositoryFiltersEntity ?? {
        fromDate: undefined,
        period: undefined,
        toDate: undefined,
        selectedFileTypes: [],
    };
    const optionsOrder = [
        {
            label: t(tKeys.repository.order.last_issued_on_last),
            value: SortBy?.[0],
        },
        {
            label: t(tKeys.repository.order.last_issued_on_first),
            value: SortBy?.[1],
        },
        {
            label: t(tKeys.filter.order.alphabetically.desc),
            value: SortBy?.[2],
        },
        {
            label: t(tKeys.filter.order.alphabetically.asc),
            value: SortBy?.[3],
        },
    ];
    const optionsGroupBy = [
        {
            label: t(tKeys.filter.order_by.document.all_documents),
            value: DocumentGroupBy?.[0],
        },
        {
            label: t(tKeys.filter.order_by.document.by_file_type),
            value: DocumentGroupBy?.[1],
        },
        {
            label: t(tKeys.filter.order_by.document.by_date),
            value: DocumentGroupBy?.[2],
        },
        {
            label: t(tKeys.filter.order_by.document.by_event),
            value: DocumentGroupBy?.[3],
        },
        {
            label: t(tKeys.filter.order_by.document.by_event_type),
            value: DocumentGroupBy?.[4],
        },
    ];
    // Find the 'by_event_type' option for initial state
    const initialGroupByOption = optionsGroupBy.find(opt => opt.value === DocumentGroupBy?.[4]) ||
        optionsGroupBy[0];
    // Initialize groupBy state based on the default tab being 'From Event'
    const [selectedOptionGroupBy, setSelectedOptionGroupBy] = useState(initialGroupByOption);
    // Queries
    // - Getters
    const { openFilePicker } = useFilePicker({
        readAs: 'DataURL',
        accept: [
            'image/jpeg',
            'image/png',
            'image/gif',
            '.txt',
            '.pdf',
            '.doc',
            '.docx',
            '.ppt',
            '.pptx',
            '.xls',
            '.xlsx',
            '.csv',
        ],
        onFilesSelected: async (chosenDocuments) => await onSelectedFileSuccess(chosenDocuments, handleOnSelectFile, onOpenCreateDocument),
    });
    const { data: documentTypesData } = useReadDocumentTypes({
        'company-id': selectedCompanyId,
    }, { query: { enabled: !!selectedCompanyId } });
    const documentSources = [
        {
            value: DocumentRelation[1],
            label: t(tKeys.filter.document_source.from_event),
        },
        {
            value: DocumentRelation[2],
            label: t(tKeys.filter.document_source.general_documents),
        },
        {
            value: DocumentRelation[3],
            label: t(tKeys.filter.document_source.to_sign),
        },
    ];
    // Default selected relation to index 1 ('From Event')
    const [selectedDocumentRelation, setSelectedDocumentRelation] = useState(documentSources[0]);
    const [fromDate, setFromDate] = useState(null);
    const [isFromPopoverOpen, setIsFromPopoverOpen] = useState(false);
    const [toDate, setToDate] = useState(null);
    const [isToPopoverOpen, setIsToPopoverOpen] = useState(false);
    const [orderBy, setOrderBy] = useState(optionsOrder?.[1]);
    const { data, isLoading: isDocumentDataLoading, refetch: refetchDocumentData, } = useReadDocumentsV2({
        'company-id': selectedCompanyId,
        'group-by': selectedOptionGroupBy?.value,
        'sort-by': orderBy?.value,
        'date-from': fromDate?.toISOString(),
        'date-to': toDate?.toISOString(),
        'document-types': selectedDocumentTypes?.length !== 0
            ? selectedDocumentTypes?.map(type => type.value)
            : documentTypeOptions?.map(type => type.value),
        'get-uri': false,
        'get-thumbnail-uri': false,
        'document-relation': selectedDocumentRelation?.value,
    }, {
        query: {
            enabled: !!selectedCompanyId && !!documentTypeOptions?.length,
        },
    });
    // Client-side filtering based on searchTerm
    const documentsData = useMemo(() => {
        const rawCategorizedData = data || [];
        if (!searchTerm) {
            return rawCategorizedData;
        }
        const lowerSearchTerm = searchTerm.toLowerCase();
        return rawCategorizedData
            .map(category => ({
            ...category,
            items: category.items?.filter(doc => {
                const titleMatch = doc.title?.toLowerCase().includes(lowerSearchTerm);
                // Assuming typeName exists on doc, similar to AI-Assist. Adjust if different.
                const typeMatch = t(doc.typeName)
                    ?.toLowerCase()
                    .includes(lowerSearchTerm);
                // Assuming eventTitle exists on doc, similar to AI-Assist. Adjust if different.
                const eventTitleMatch = doc.eventTitle
                    ?.toLowerCase()
                    .includes(lowerSearchTerm);
                return titleMatch || typeMatch || eventTitleMatch;
            }),
        }))
            .filter(category => category.items && category.items.length > 0); // Keep categories with matching items
    }, [data, searchTerm]);
    const selectedRepositoryFilters = useMemo(() => {
        const isAllFileTypeSelected = repositoryFiltersEntity?.selectedFileTypes?.length ===
            documentTypesData?.length;
        const isPeriodSelected = !!repositoryFiltersEntity?.period;
        const allFilters = Object.values(repositoryFiltersEntity).filter(value => !!value)?.length;
        return (allFilters + (isAllFileTypeSelected && -1) + (isPeriodSelected && -2));
    }, [repositoryFiltersEntity]);
    // Functions
    function refetchDocuments() {
        void refetchDocumentData();
    }
    function handleOnSelectFile(value) {
        setSelectedFile(value);
    }
    function handleOnGroupByChange(groupBy) {
        setSelectedOptionGroupBy(groupBy);
    }
    function onLayoutIconPress() {
        setListLayoutType(prevLayoutType => prevLayoutType === ListLayoutEnum.ROW
            ? ListLayoutEnum.GRID
            : ListLayoutEnum.ROW);
    }
    function handleOnPressTab(activeKey) {
        setSelectedTab(activeKey);
        const selectedSource = documentSources[activeKey];
        handleOnDocumentRelationChange(selectedSource);
        // Set grouping based on the selected tab
        if (selectedSource.value === DocumentRelation[1]) {
            // From Event
            // Find 'by_event_type' or default to something sensible if not found
            const eventTypeOption = optionsGroupBy.find(opt => opt.value === DocumentGroupBy?.[4]);
            setSelectedOptionGroupBy(eventTypeOption || optionsGroupBy[0]);
        }
        else {
            // Reset to default or another appropriate grouping for other tabs
            setSelectedOptionGroupBy(optionsGroupBy[1]); // Example: Reset to 'by_file_type'
        }
    }
    function onDocumentPress(document) {
        const routeParams = {
            documentKind: isMinute(document)
                ? DocumentKindEnum.MINUTES
                : DocumentKindEnum.DOCUMENT,
            documentId: document.id,
            eventId: document.eventId,
        };
        push('/company/document-details', {
            companyId: selectedCompanyId,
            ...routeParams,
        });
    }
    function handleOnDocumentTypeFilterChange(documentTypes) {
        setSelectedDocumentTypes(documentTypes);
    }
    function handleOnDocumentRelationChange(documentSource) {
        setSelectedDocumentRelation(documentSource);
    }
    function handleOnSetFromDateChange(fromDate) {
        setFromDate(fromDate);
    }
    function onToggleFromPopOver() {
        setIsFromPopoverOpen(!isFromPopoverOpen);
    }
    function handleOnSetToDateChange(toDate) {
        setToDate(toDate);
    }
    function onToggleToPopOver() {
        setIsToPopoverOpen(!isToPopoverOpen);
    }
    function handleOnPressAddDocument() {
        openFilePicker();
    }
    function handleOnCloseModal() {
        setShowModal(false);
    }
    async function handleOnSelectDocument(documentType) {
        await handleSelectDocument(documentType, push, handleOnCloseModal, '/create-document/default');
    }
    function onSearchPress() {
        push('/search');
    }
    const canUploadDocument = selectedEnrolment?.role === Role[0] || selectedEnrolment?.role === Role[1];
    // Effects
    useEffect(() => {
        if (documentTypesData) {
            const options = documentTypesData.map(docType => ({
                label: t(docType.name),
                value: docType.id,
            }));
            setDocumentTypeOptions(options);
        }
    }, [documentTypesData, t]);
    // Refetch anytime the params are changing
    useEffect(() => {
        if (params)
            void refetchDocumentData();
    }, [params]);
    // Return
    return {
        t,
        optionsGroupBy,
        handleOnPressTab,
        selectedTab,
        selectedRepositoryFilters,
        handleOnPressAddDocument,
        documentsData,
        isDocumentDataLoading,
        isDropdownOpen,
        listLayoutType,
        onDocumentPress,
        onLayoutIconPress,
        optionsOrder,
        refetchDocumentData,
        setDropdownOpen,
        isOpenCreateDocument,
        onCloseCreateDocument,
        selectedFile,
        refetchDocuments,
        canUploadDocument,
        setOrderBy,
        orderBy,
        documentTypeOptions,
        documentSources,
        handleOnDocumentTypeFilterChange,
        handleOnDocumentRelationChange,
        handleOnSetFromDateChange,
        fromDate,
        onToggleFromPopOver,
        isFromPopoverOpen,
        handleOnSetToDateChange,
        toDate,
        onToggleToPopOver,
        isToPopoverOpen,
        handleOnGroupByChange,
        selectedOptionGroupBy,
        selectedDocumentRelation,
        selectedDocumentTypes,
        searchTerm,
        setSearchTerm,
    };
};
export default useLogic;
